import React, { useState, useEffect, useRef } from 'react';
import './UploadPost.css';
import '../../compos/SinglePostHelper/SinglePostHelper.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { BACKEND_URL, profilePicUrl } from '../../config';
import ExploreMap from '../../compos/AllPosts/ExploreMap';
import {
	Image as ImageIcon,
	Trash2,
	Eye,
	Send,
	ArrowLeft,
	Info,
	ChevronDown,
	Check,
	Tag,
	X,
	Gauge,
	Heart,
	Share2,
} from 'lucide-react';
import { useUser } from '../../UserContext';
import DefaultPFP from '../../Media/Icons/DefaultPFP.webp';

const getYoutubeEmbedUrl = (url: string) => {
	if (!url) return null;
	const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
	const match = url.match(regExp);
	if (match && match[2].length === 11) {
		return `https://www.youtube.com/embed/${match[2]}`;
	}
	return null;
};

interface ConfirmModalProps {
	show: boolean;
	onConfirm: () => void;
	onCancel: () => void;
	isSubmitting: boolean;
}

const ConfirmPublishModal: React.FC<ConfirmModalProps> = ({ show, onConfirm, onCancel, isSubmitting }) => {
	if (!show) return null;

	return (
		<div className='fixed inset-0 z-[2000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm'>
			<div className='bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden'>
				<div className='p-8 text-center'>
					<div className='w-16 h-16 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-6'>
						<Send className='w-8 h-8' />
					</div>
					<h3 className='text-2xl font-bold text-gray-900 mb-2'>Confirm Publication?</h3>
					<p className='text-gray-600 mb-8 text-lg'>
						Are you sure you want to publish this post? Once live, it will be visible to all users on the platform.
					</p>

					<div className='flex flex-col gap-3'>
						<button
							onClick={onConfirm}
							disabled={isSubmitting}
							className='w-full py-4 bg-blue-600 text-white font-bold rounded-2xl hover:bg-blue-700 transition-all flex items-center justify-center gap-2 text-lg shadow-lg shadow-blue-200 disabled:opacity-70 disabled:cursor-not-allowed'
						>
							{isSubmitting ? (
								<>
									<div className='w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin' />
									Publishing...
								</>
							) : (
								<>
									Publish Officially <Send className='w-5 h-5' />
								</>
							)}
						</button>
						<button
							onClick={onCancel}
							disabled={isSubmitting}
							className='w-full py-4 bg-gray-100 text-gray-700 font-bold rounded-2xl hover:bg-gray-200 transition-all text-lg disabled:opacity-50'
						>
							Cancel
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

interface UploadPostProps {
	onNavigateAway?: (cancelFn: () => void) => void;
}

const UploadPost = ({ onNavigateAway }: UploadPostProps) => {
	const { user } = useUser();
	const [postData, setPostData] = useState({
		mediaIds: [] as string[], // Initialize as an empty array
		title: '',
		body: '',
		tags: '',
		isAnonymous: false,
	});

	const [selectedTags, setSelectedTags] = useState<string[]>([]);

	const [mediaData, setMediaData] = useState({
		file: null as File | null,
		latitude: '25',
		longitude: '77',
		device: '',
		mac: '',
		ip: '',
		youtubeLink: '',
	});

	const [showAlert, setShowAlert] = useState(false);
	const [postPublished, setPostPublished] = useState(false);
	const [toastCountdown, setToastCountdown] = useState(5);
	const [isPreview, setIsPreview] = useState(false);
	const [showConfirmPublish, setShowConfirmPublish] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isTagMenuOpen, setIsTagMenuOpen] = useState(false);
	const tagMenuRef = useRef<HTMLDivElement>(null);
	const mediaSectionRef = useRef<HTMLDivElement>(null);
	const [mediaError, setMediaError] = useState<string | null>(null);
	const [tagError, setTagError] = useState<string | null>(null);
	const [titleError, setTitleError] = useState<string | null>(null);
	const [bodyError, setBodyError] = useState<string | null>(null);
	const [activeMediaType, setActiveMediaType] = useState<'image' | 'youtube'>('image');
	// YouTube link validation: 'idle' | 'validating' | 'valid' | 'invalid'
	const [ytLinkStatus, setYtLinkStatus] = useState<'idle' | 'validating' | 'valid' | 'invalid'>('idle');
	// Ref set to true by AdminPanel when it switches away from this tab
	const cancelRedirectRef = useRef(false);

	// Register cancel function with parent (AdminPanel) if running inside it
	useEffect(() => {
		if (onNavigateAway) {
			onNavigateAway(() => {
				cancelRedirectRef.current = true;
			});
		}
	}, [onNavigateAway]);

	const handleTabChange = (type: 'image' | 'youtube') => {
		setActiveMediaType(type);
		setMediaError(null);
		// Optional: Clear the other field to avoid accidental submissions/confusion
		if (type === 'image') {
			setMediaData((prev) => ({ ...prev, youtubeLink: '' }));
			setYtLinkStatus('idle');
		} else {
			setImage(null);
			setImageURL('');
			setMediaData((prev) => ({ ...prev, file: null }));
		}
	};

	const availableTags = [
		'General News',
		'Research',
		'Crime',
		'Fraud',
		'Politics',
		'Initiatives',
		'Opinions',
		'Health',
		'Kidjiu',
	];

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (tagMenuRef.current && !tagMenuRef.current.contains(event.target as Node)) {
				setIsTagMenuOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [isPreview]);

	// Validate YouTube link existence via oEmbed (no API key needed)
	useEffect(() => {
		const link = mediaData.youtubeLink.trim();
		if (activeMediaType !== 'youtube' || !link) {
			setYtLinkStatus('idle');
			return;
		}
		// Only bother if it parses to an embed URL
		if (!getYoutubeEmbedUrl(link)) {
			setYtLinkStatus('invalid');
			return;
		}
		setYtLinkStatus('validating');
		const timer = setTimeout(async () => {
			try {
				const res = await fetch(`https://www.youtube.com/oembed?url=${encodeURIComponent(link)}&format=json`);
				setYtLinkStatus(res.ok ? 'valid' : 'invalid');
			} catch {
				setYtLinkStatus('invalid');
			}
		}, 600); // debounce 600 ms
		return () => clearTimeout(timer);
	}, [mediaData.youtubeLink, activeMediaType]);

	useEffect(() => {
		// Device info (best-effort, informational only).
		const userAgent = navigator.userAgent;
		setMediaData((prevData) => ({
			...prevData,
			device: userAgent,
			// The IP is now derived server-side from the request (the backend
			// trusts the nginx proxy for the real client address). We no longer
			// call the third-party api.ipify.org — it was blocked by adblockers,
			// and a client-supplied IP is spoofable and was ignored server-side.
			ip: '',
			mac: '',
		}));
	}, []);

	const navigate = useNavigate();
	const location = useLocation();

	const handleCreatePost = async () => {
		setIsSubmitting(true);

		const mediaFormData = new FormData();
		mediaFormData.append('latitude', mediaData.latitude);
		mediaFormData.append('longitude', mediaData.longitude);
		mediaFormData.append('device', mediaData.device);
		mediaFormData.append('mac', mediaData.mac);
		mediaFormData.append('ip', mediaData.ip);
		// Tell the backend this upload is for a post so it can enforce the daily
		// post quota BEFORE the file is uploaded, instead of after (wasting the
		// upload when the day's quota is already spent).
		mediaFormData.append('purpose', 'post');

		// Only send the relevant media field for the active toggle tab
		if (activeMediaType === 'youtube') {
			mediaFormData.append('youtubeLink', mediaData.youtubeLink.trim());
		} else if (activeMediaType === 'image' && mediaData.file) {
			mediaFormData.append('file', mediaData.file);
		}

		try {
			// Upload media data
			const mediaResponse = await fetch(`${BACKEND_URL}/api/createMedia`, {
				method: 'POST',
				body: mediaFormData,
				credentials: 'include' as RequestCredentials,
				headers: {
				},
			});

			const mediaResult = await mediaResponse.json();
			console.log('This is mediaResult:', mediaResult);

			if (!mediaResponse.ok) {
				throw new Error(mediaResult?.message || `Media upload failed (${mediaResponse.status})`);
			}

			if (mediaResult) {
				console.log('media created successfully, this is media ID', mediaResult);
			}

			// await updatePostData(mediaId);

			// const postDataToSend =  {
			//     mediaIds: postData.mediaIds.map(id => id.toString()),
			//     title: postData.title,
			//     body: postData.body,
			//     tags: postData.tags,
			//     isAnonymous: postData.isAnonymous,
			// };
			const postDataToSend = {
				mediaIds: [Number(mediaResult)], // send as number, not string
				title: postData.title,
				body: postData.body,
				tags: selectedTags.join(', '),
				isAnonymous: postData.isAnonymous,
			};
			console.log('This is mediaIds of postData to send:', postDataToSend.mediaIds);
			// Submit post data
			const postResponse = await fetch(`${BACKEND_URL}/api/createPost`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(postDataToSend),
				credentials: 'include' as RequestCredentials,
			});

			const postResult = await postResponse.json();
			console.log('createPost response:', postResponse.status, postResult);

			if (!postResponse.ok) {
				throw new Error(
					(Array.isArray(postResult?.message) ? postResult.message.join(', ') : postResult?.message) ||
						`Post creation failed (${postResponse.status})`
				);
			}

			setShowAlert(true);
			setPostPublished(true);
			setShowConfirmPublish(false);
			setToastCountdown(5);
			let remaining = 5;
			const interval = setInterval(() => {
				remaining -= 1;
				setToastCountdown(remaining);
				if (remaining <= 0) {
					clearInterval(interval);
					setShowAlert(false);
					// Redirect only if still on this page AND the admin panel hasn't switched tabs
					if (!cancelRedirectRef.current && window.location.pathname === location.pathname) {
						navigate('/news');
					}
				}
			}, 1000);
		} catch (error: any) {
			console.error('Error creating post:', error);
			setShowConfirmPublish(false);
			alert(`Failed to publish: ${error?.message || 'Unknown error'}`);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleInputChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement> | React.ChangeEvent<HTMLTextAreaElement>
	) => {
		const { name, value } = e.target;
		setTitleError(null);
		setBodyError(null);
		if (name === 'youtubeLink') {
			setMediaError(null);
			setMediaData((prevData) => ({
				...prevData,
				youtubeLink: value,
			}));
		} else {
			setPostData((prevData) => ({
				...prevData,
				[name]: value,
			}));
		}
	};

	const [image, setImage] = useState<File | null>(null);
	const [imageURL, setImageURL] = useState<string>('');

	// Ref to reference the file input element
	const fileInputRef = useRef<HTMLInputElement | null>(null);

	// Handle file selection (upload)
	const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files ? event.target.files[0] : null;
		if (file) {
			setMediaError(null);
			setImage(file);
			setImageURL(URL.createObjectURL(file)); // Create a URL for the uploaded image
			// Also update mediaData.file
			setMediaData((prevData) => ({
				...prevData,
				file: file,
			}));
		}
	};

	// Handle image deletion
	const handleImageDelete = () => {
		setImage(null); // Clear the image state
		setImageURL(''); // Clear the image URL
		// Clear mediaData.file
		setMediaData((prevData) => ({
			...prevData,
			file: null,
		}));
		// Reset the file input value using ref
		if (fileInputRef.current) {
			fileInputRef.current.value = ''; // Changed from innerText to value
		}
	};

	const toggleTag = (tag: string) => {
		setTagError(null);
		if (selectedTags.includes(tag)) {
			setSelectedTags(selectedTags.filter((t) => t !== tag));
		} else {
			if (selectedTags.length >= 2) {
				setTagError('Only two tags can be selected');
				return;
			}
			setSelectedTags([...selectedTags, tag]);
		}
	};

	return (
		<div className='max-w-6xl mx-auto px-4 pb-5'>
			<ConfirmPublishModal
				show={showConfirmPublish}
				onConfirm={handleCreatePost}
				onCancel={() => setShowConfirmPublish(false)}
				isSubmitting={isSubmitting}
			/>

			<h1 className='text-3xl font-extrabold text-blue-900 mb-8'>Create Post</h1>

			<div className='bg-white p-8 rounded-2xl shadow-sm border border-gray-100'>
				{isPreview ? (
					<div className='preview-mode animate-fade-in py-8 bg-[#f0f2f5] -m-8 rounded-2xl'>
						{/* IDENTICAL SINGLEPOST REPLICA */}
						<div className='sph-outer'>
							<div className='sph-card !shadow-lg'>
								{/* HEADER */}
								<div className='sph-header'>
									<div className='sph-header-left'>
										<div className='sph-avatar'>
											<img
												src={user?.profilepicURL ? profilePicUrl(user.profilepicURL) : DefaultPFP}
												alt='Profile'
											/>
										</div>
										<span className='sph-sender-name'>{user?.name || 'Admin'}</span>
									</div>

									<div className='sph-header-right'>
										<div className='sph-meta'>
											<span className='sph-meta-row'>
												<span className='sph-meta-label'>Created on:</span>
												<span className='sph-meta-value'>xx-xx-xxxx, 00:00 (Just now)</span>
											</span>
											<span className='sph-meta-row'>
												<span className='sph-meta-label'>Location:</span>
												<div className='sph-location-container'>
													<span className='cursor-default'>
														<svg
															width='16'
															height='16'
															viewBox='0 0 24 24'
															fill='#e74c3c'
															xmlns='http://www.w3.org/2000/svg'
														>
															<path d='M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z' />
														</svg>
													</span>
												</div>
											</span>
										</div>
										<div className='sph-vbar' />
										<div className='sph-dots-btn !cursor-default hover:!bg-transparent'>
											<svg width='18' height='18' viewBox='0 0 24 24' fill='currentColor'>
												<circle cx='12' cy='5' r='1.5' />
												<circle cx='12' cy='12' r='1.5' />
												<circle cx='12' cy='19' r='1.5' />
											</svg>
										</div>
									</div>
								</div>

								{/* IMAGE / VIDEO */}
								<div className='sph-image-wrap'>
									{activeMediaType === 'youtube' &&
									mediaData.youtubeLink &&
									getYoutubeEmbedUrl(mediaData.youtubeLink) ? (
										<div className='sph-video-wrap'>
											<iframe
												src={getYoutubeEmbedUrl(mediaData.youtubeLink) || ''}
												width='100%'
												height='420px'
												frameBorder='0'
												title='YouTube Video'
												className='w-full border-none'
												allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; compute-pressure'
											/>
										</div>
									) : (
										<div className='relative aspect-[4/3] flex items-center justify-center overflow-hidden bg-gray-100'>
											{imageURL ? (
												<img
													src={imageURL}
													alt='Post preview'
													className='absolute inset-0 w-full h-full object-cover'
												/>
											) : (
												<div className='text-gray-400'>No media attached</div>
											)}
										</div>
									)}
								</div>

								{/* BODY */}
								<div className='sph-body'>
									<div className='flex justify-between items-start gap-3'>
										<h2 className='sph-title'>{postData.title || 'Untitled Post'}</h2>
										<div className='flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 text-amber-600 border border-amber-100 shrink-0 mb-2 text-sm font-semibold'>
											<Gauge size={16} />
											<span>Risk</span>
										</div>
									</div>

									<p
										className={`sph-description ${!postData.body ? 'sph-description--empty' : ''} whitespace-pre-wrap`}
									>
										{postData.body || 'No description provided.'}
									</p>

									{selectedTags.length > 0 && (
										<div className='flex flex-wrap gap-1 mt-2 mb-2'>
											{selectedTags.map((tag) => (
												<span key={tag} className='text-blue-600 text-opacity-80 text-sm font-medium leading-none mr-2'>
													#{tag.trim()}
												</span>
											))}
										</div>
									)}
								</div>

								<div className='sph-divider' />

								{/* ACTION BAR (Simplified for Preview) */}
								<div className='flex flex-nowrap items-center px-4 py-3 gap-3 opacity-60 pointer-events-none'>
									<div className='flex-1 flex gap-2 min-w-0 bg-gray-50 rounded-full pr-1 mr-1 border border-gray-200 items-center'>
										<span className='flex-1 bg-transparent text-sm px-3 py-2 text-gray-400'>Write a comment…</span>
										<div className='shrink-0 w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center'>
											<Send size={14} />
										</div>
									</div>
									<div className='flex items-center gap-4 shrink-0 border-l border-gray-200 pl-4 py-1'>
										<div className='flex items-center gap-1.5 text-gray-500'>
											<span className='font-semibold text-sm'>0</span>
											<Heart className='w-5 h-5' />
										</div>
										<div className='text-gray-500'>
											<Share2 className='w-5 h-5' />
										</div>
									</div>
								</div>
							</div>
						</div>

						<div className='sph-outer mt-8 mb-0'>
							<div className='w-full max-w-[700px] flex flex-row gap-4 items-center'>
								<button
									type='button'
									onClick={() => !postPublished && setIsPreview(false)}
									disabled={postPublished}
									className={`flex-1 px-8 py-3 border-2 font-bold rounded-xl transition-colors flex items-center justify-center gap-2 text-lg ${
										postPublished
											? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
											: 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
									}`}
								>
									<ArrowLeft className='w-5 h-5' /> Edit Post
								</button>
								<button
									type='button'
									onClick={() => !postPublished && setShowConfirmPublish(true)}
									disabled={postPublished}
									className={`flex-1 px-8 py-3 border-2 border-transparent font-bold rounded-xl flex items-center justify-center gap-2 text-lg transition-colors ${
										postPublished
											? 'bg-gray-300 text-gray-500 cursor-not-allowed shadow-none'
											: 'bg-blue-600 text-white shadow-lg hover:bg-blue-700'
									}`}
								>
									Publish Now <Send className='w-5 h-5' />
								</button>
							</div>
						</div>
					</div>
				) : (
					<form
						onSubmit={(e) => {
							e.preventDefault();
							setMediaError(null);
							setTagError(null);
							setTitleError(null);
							setBodyError(null);

							const hasImage = !!image;
							const hasYoutube = !!mediaData.youtubeLink.trim();

							// TOP-DOWN VALIDATION ORDER:
							// 1. MEDIA
							if (activeMediaType === 'image' && !hasImage) {
								setMediaError('Please upload an image to continue.');
								mediaSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
								return;
							}

							if (activeMediaType === 'youtube' && !hasYoutube) {
								setMediaError('Please enter a YouTube link to continue.');
								mediaSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
								return;
							}

							if (activeMediaType === 'youtube' && ytLinkStatus === 'validating') {
								// Inline status already shows "Verifying…" — just prevent navigation
								mediaSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
								return;
							}

							if (activeMediaType === 'youtube' && ytLinkStatus !== 'valid') {
								// Inline status already shows the red error — just prevent navigation
								mediaSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
								return;
							}

							// 2. HEADLINE
							if (!postData.title.trim()) {
								setTitleError('Headline is required');
								document
									.getElementById('formGroupExampleInput')
									?.scrollIntoView({ behavior: 'smooth', block: 'center' });
								return;
							}

							// 3. DESCRIPTION
							if (!postData.body.trim()) {
								setBodyError('Description is required');
								document.getElementById('user-input')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
								return;
							}

							// 4. TAGS
							if (selectedTags.length === 0) {
								setTagError('Select at least one tag');
								setIsTagMenuOpen(true);
								tagMenuRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
								return;
							}
							if (selectedTags.length > 2) {
								setTagError('Only two tags can be selected');
								setIsTagMenuOpen(true);
								tagMenuRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
								return;
							}

							setIsPreview(true);
						}}
					>
						{/* MEDIA REGION */}
						<div ref={mediaSectionRef} className='space-y-6 mb-8'>
							<div
								className={`w-full bg-gray-100/80 border ${mediaError ? 'border-red-500 ring-2 ring-red-50' : 'border-gray-200'} rounded-2xl flex items-center h-[54px] relative overflow-hidden backdrop-blur-sm p-1.5 transition-all`}
							>
								{/* Sliding Background Indicator */}
								<div
									className={`absolute top-1.5 bottom-1.5 w-[calc(50%-9px)] bg-white shadow-sm border border-gray-200/50 rounded-xl transition-all duration-300 ease-out z-0 ${activeMediaType === 'youtube' ? 'left-[calc(50%+3px)]' : 'left-1.5'}`}
								/>

								<button
									type='button'
									onClick={() => handleTabChange('image')}
									className={`relative z-10 flex-1 h-full font-bold text-sm transition-colors duration-300 flex items-center justify-center gap-2 ${activeMediaType === 'image' ? 'text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
								>
									<ImageIcon size={18} />
									Upload Image
								</button>
								<button
									type='button'
									onClick={() => handleTabChange('youtube')}
									className={`relative z-10 flex-1 h-full font-bold text-sm transition-colors duration-300 flex items-center justify-center gap-2 ${activeMediaType === 'youtube' ? 'text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
								>
									<svg
										width='18'
										height='18'
										viewBox='0 0 24 24'
										fill='none'
										stroke='currentColor'
										strokeWidth='2'
										strokeLinecap='round'
										strokeLinejoin='round'
									>
										<path d='M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z' />
										<polygon points='9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02' />
									</svg>
									YouTube Link
								</button>
							</div>

							<div className='animate-in fade-in duration-300'>
								{mediaError && (
									<div className='text-red-500 text-xs font-bold mb-2 animate-fade-in flex items-center gap-1.5 uppercase tracking-wider'>
										<Info size={14} />
										{mediaError}
									</div>
								)}
								{activeMediaType === 'image' ? (
									<div>
										{!image ? (
											<div className='p-4 rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50/50 hover:bg-gray-100/80 transition-all duration-300 relative group flex flex-col items-center justify-center cursor-pointer'>
												<input
													className='absolute inset-0 w-full h-full opacity-0 cursor-pointer z-20'
													type='file'
													ref={fileInputRef}
													accept='image/*'
													onChange={handleImageUpload}
												/>
												<div className='w-full flex flex-col items-center justify-center aspect-[16/9] xl:aspect-[16/6]'>
													<div className='w-14 h-14 bg-white shadow-sm rounded-full flex items-center justify-center text-gray-400 mb-4 group-hover:scale-110 group-hover:text-blue-600 group-hover:shadow-md transition-all duration-300'>
														<ImageIcon className='w-7 h-7' />
													</div>
													<span className='font-bold text-gray-400 group-hover:text-blue-600 transition-colors text-sm tracking-widest uppercase'>
														Click to upload image
													</span>
												</div>
												{/* Matching spacer for footers below to keep height stable */}
												<div className='text-center mt-4 invisible'>
													<div className='px-4 py-2 text-xs font-bold uppercase'>Spacer</div>
												</div>
											</div>
										) : (
											<div className='flex flex-col xl:flex-row gap-6 p-4 rounded-2xl border border-gray-100'>
												{/* Image Preview Half */}
												<div className='flex-1 flex flex-col w-full'>
													<div className='bg-gray-100 rounded-xl border border-gray-200 w-full aspect-[4/3] relative flex items-center justify-center overflow-hidden group shadow-sm'>
														<img src={imageURL} alt='Uploaded' className='w-full h-full object-cover' />
														<button
															type='button'
															onClick={handleImageDelete}
															title='Remove Image'
															className='absolute top-3 right-3 bg-white/90 text-red-600 p-2.5 font-bold rounded-full shadow-lg flex items-center justify-center hover:bg-red-50 hover:scale-110 transition-all opacity-0 group-hover:opacity-100'
														>
															<Trash2 className='w-5 h-5' />
														</button>
													</div>
													<div className='text-center mt-4'>
														<button
															type='button'
															onClick={handleImageDelete}
															className='inline-flex items-center justify-center gap-2 text-xs font-bold text-red-500 tracking-widest uppercase px-4 py-2 rounded-lg hover:bg-red-50 transition-all duration-200'
														>
															<Trash2 size={15} />
															Remove Image
														</button>
													</div>
												</div>
												{/* Map Selection Half */}
												<div className='flex-1 flex flex-col w-full min-w-[300px]'>
													<div className='bg-white rounded-xl border border-gray-200 w-full aspect-[4/3] overflow-hidden shadow-sm relative [&>div]:!mb-0'>
														{mediaData.latitude && mediaData.longitude ? (
															<ExploreMap
																coords={{ lat: parseFloat(mediaData.latitude), lng: parseFloat(mediaData.longitude) }}
																setCoords={(newCoords: any) =>
																	setMediaData((prev) => ({
																		...prev,
																		latitude: newCoords.lat.toString(),
																		longitude: newCoords.lng.toString(),
																	}))
																}
																simplified={true}
																radius={4}
															/>
														) : (
															<div className='w-full h-full flex flex-col items-center justify-center text-gray-400 p-6 text-center bg-gray-100/50'>
																<div className='animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400 mb-4'></div>
																Fetching your location...
															</div>
														)}
													</div>
													<div className='text-center mt-4'>
														<span className='inline-flex items-center justify-center text-xs font-bold text-gray-400 tracking-widest uppercase px-4 py-2'>
															Click map to set precise pin location
														</span>
													</div>
												</div>
											</div>
										)}
									</div>
								) : (
									<div>
										<input
											type='text'
											id='youtubeInput'
											className={`w-full py-3 px-4 bg-gray-50 border ${
												ytLinkStatus === 'invalid'
													? 'border-red-500 ring-2 ring-red-50'
													: ytLinkStatus === 'valid'
														? 'border-green-500 ring-2 ring-green-50'
														: 'border-gray-200'
											} rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all font-normal text-[15px] placeholder:text-gray-400 text-gray-700 leading-normal font-sans tracking-normal`}
											style={{ fontFamily: 'inherit' }}
											name='youtubeLink'
											placeholder='https://youtube.com/watch?v=...   OR   https://youtu.be/...'
											value={mediaData.youtubeLink}
											onChange={handleInputChange}
										/>
										{mediaData.youtubeLink.trim() && (
											<div
												className={`mt-2 text-xs font-bold flex items-center gap-1.5 uppercase tracking-wider ${
													ytLinkStatus === 'validating'
														? 'text-gray-400'
														: ytLinkStatus === 'valid'
															? 'text-green-600'
															: ytLinkStatus === 'invalid'
																? 'text-red-500'
																: 'text-gray-400'
												}`}
											>
												{ytLinkStatus === 'validating' && (
													<>
														<div className='w-3 h-3 border-2 border-gray-300 border-t-gray-500 rounded-full animate-spin' />{' '}
														Verifying video…
													</>
												)}
												{ytLinkStatus === 'valid' && (
													<>
														<Check size={13} /> Video found — looks good!
													</>
												)}
												{ytLinkStatus === 'invalid' && (
													<>
														<Info size={13} /> Video not found or invalid link. Please check the URL.
													</>
												)}
											</div>
										)}
									</div>
								)}
							</div>
						</div>

						<div className='mb-6'>
							<label className='block text-gray-700 font-bold mb-2 text-lg text-left' htmlFor='formGroupExampleInput'>
								Headline
							</label>
							{titleError && (
								<div className='text-red-500 text-xs font-bold mb-2 animate-fade-in flex items-center gap-1.5 uppercase tracking-wider'>
									<Info size={14} />
									{titleError}
								</div>
							)}
							<input
								type='text'
								className={`w-full py-3 px-4 bg-gray-50 border ${titleError ? 'border-red-500 ring-2 ring-red-50' : 'border-gray-200'} rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all font-normal text-[15px] placeholder:text-gray-400 text-gray-700 leading-normal font-sans tracking-normal`}
								style={{ fontFamily: 'inherit' }}
								id='formGroupExampleInput'
								name='title'
								placeholder='Enter a clear, descriptive headline...'
								value={postData.title}
								onChange={handleInputChange}
							/>
						</div>
						<div className='mb-6'>
							<label className='block text-gray-700 font-bold mb-2 text-lg text-left' htmlFor='user-input'>
								Detailed Description
							</label>
							{bodyError && (
								<div className='text-red-500 text-xs font-bold mb-2 animate-fade-in flex items-center gap-1.5 uppercase tracking-wider'>
									<Info size={14} />
									{bodyError}
								</div>
							)}
							<textarea
								id='user-input'
								rows={6}
								name='body'
								className={`w-full py-3 px-4 bg-gray-50 border ${bodyError ? 'border-red-500 ring-2 ring-red-50' : 'border-gray-200'} rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white transition-all font-normal resize-none text-[15px] placeholder:text-gray-400 text-gray-700 leading-normal font-sans tracking-normal`}
								style={{ fontFamily: 'inherit' }}
								placeholder='Provide the full story or alert details here...'
								value={postData.body}
								onChange={handleInputChange}
							/>
						</div>

						<div className='mb-6 relative' ref={tagMenuRef}>
							<label className='block text-gray-700 font-bold mb-2 text-lg text-left'>Category Tags</label>

							{tagError && (
								<div className='text-red-500 text-xs font-bold mb-2 animate-fade-in flex items-center gap-1.5 uppercase tracking-wider'>
									<Info size={14} />
									{tagError}
								</div>
							)}

							<div
								onClick={() => {
									setIsTagMenuOpen(!isTagMenuOpen);
									setTagError(null);
								}}
								className={`w-full py-3 px-4 bg-gray-50 border ${tagError ? 'border-red-500 ring-2 ring-red-50' : isTagMenuOpen ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-200'} rounded-xl cursor-pointer flex items-center justify-between transition-all hover:bg-white`}
							>
								<div className='flex items-center gap-2'>
									<Tag className='w-4 h-4 text-gray-400' />
									<span className='text-gray-400 font-normal text-[15px] leading-normal'>
										{selectedTags.length > 0 ? `${selectedTags.length} tags selected` : 'Select categories...'}
									</span>
								</div>
								<ChevronDown
									className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${isTagMenuOpen ? 'rotate-180' : ''}`}
								/>
							</div>

							<div
								className={`grid transition-all duration-300 ease-in-out ${isTagMenuOpen ? 'grid-rows-[1fr] opacity-100 mt-2' : 'grid-rows-[0fr] opacity-0 mt-0'}`}
							>
								<div className='overflow-hidden bg-white border border-gray-100 rounded-2xl'>
									<div className='py-2 px-2'>
										<div className='grid grid-cols-2 gap-1'>
											{availableTags.map((tag) => (
												<div
													key={tag}
													onClick={() => toggleTag(tag)}
													className={`flex items-center justify-between px-4 py-2.5 rounded-xl cursor-pointer transition-all ${selectedTags.includes(tag) ? 'bg-blue-50 text-blue-700 font-bold' : 'hover:bg-gray-50 text-gray-600'}`}
												>
													<span className='text-sm'>{tag}</span>
													{selectedTags.includes(tag) && <Check className='w-4 h-4' />}
												</div>
											))}
										</div>
									</div>
								</div>
							</div>

							{selectedTags.length > 0 && (
								<div className='flex flex-wrap gap-2 mt-3 mb-8'>
									{selectedTags.map((tag) => (
										<div
											key={tag}
											onClick={() => toggleTag(tag)}
											className='group flex items-center bg-blue-50 text-blue-700 pl-4 pr-1 py-1 rounded-full text-sm font-bold shadow-sm transition-all border border-blue-100 cursor-pointer hover:bg-red-50 hover:text-red-700 hover:border-red-100'
										>
											<span className='mr-2'>{tag}</span>
											<div className='w-6 h-6 rounded-full flex items-center justify-center group-hover:bg-red-200/50 transition-colors'>
												<X size={14} strokeWidth={3} />
											</div>
										</div>
									))}
								</div>
							)}
						</div>

						<div className='flex justify-center'>
							<button
								type='submit'
								className='w-full px-8 py-3 bg-blue-600 text-white font-bold rounded-xl shadow-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 text-lg'
							>
								<Eye className='w-5 h-5' /> Preview Post
							</button>
						</div>
					</form>
				)}

				{showAlert && (
					<div className='fixed bottom-6 right-6 z-50 w-[280px] overflow-hidden rounded-xl shadow-2xl bg-green-600 text-white'>
						<div className='flex items-center gap-3 px-4 py-3'>
							<div className='w-8 h-8 bg-white/20 rounded-full flex items-center justify-center shrink-0'>
								<svg className='w-5 h-5 text-white' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
									<path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M5 13l4 4L19 7'></path>
								</svg>
							</div>
							<div>
								<div className='font-bold text-sm leading-tight'>Post successfully published!</div>
								<div className='text-white/75 text-xs mt-0.5'>Redirecting to feed in {toastCountdown}s</div>
							</div>
						</div>
						{/* Progress bar — pure CSS animation perfectly in sync with the 5s timer */}
						<div className='h-[3px] bg-white/20 w-full'>
							<div className='h-full bg-white/70' style={{ animation: 'toast-shrink 5s linear forwards' }} />
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default UploadPost;
